/* eslint-disable @typescript-eslint/no-unsafe-argument */
import {
  Table as ChakraTable,
  Flex,
  LayoutProps,
  Tbody,
  Text,
  Thead,
} from "@chakra-ui/react";
import { ChakraProps } from "@chakra-ui/system";
import { Meta } from "@intentsify/types";
import { ErrorBoundary } from "@sentry/react";
import {
  ColumnDef,
  ExpandedState,
  PaginationState,
  RowSelectionState,
  Row as RowType,
  SortingState,
  getCoreRowModel,
  getExpandedRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { Button } from "components";
import React, { ReactNode, useEffect, useMemo, useRef, useState } from "react";
import { useTableColumnVisibility } from "store/columnVisibility.hooks";
import { useAppStore } from "store/useAppStore";
import { FetchDataParams } from "types";
import { useDeepEffect } from "utils";
import { getColumnId, isColumnHideable } from "../columnVisibility.utils";
import { Loader } from "../../Loader";
import { NoData } from "../../NoData";
import { HeaderGroup, Pagination, Row, TokenPagination } from "../components";
import { PaginationType } from "./Table.types";
import {
  getFetchDataParams,
  getTablePaginationParams,
  getTokenFetchDataParams,
} from "./Table.utils";
import { useTokenPagination } from "./hooks";
import { byLook } from "theme/look";

type TableProps<T extends Record<string, unknown>> = {
  dataId?: string;
  data: T[];
  meta?: Meta; // Meta is undefined while request is not fulfilled.
  columns: ColumnDef<T>[];
  searchValue?: string;
  noDataMessage?: ReactNode;
  customNoData?: ReactNode;
  showNoData?: boolean;

  isFetching?: boolean;
  isScrollable?: boolean;
  isExpandable?: boolean;
  paginationType?: PaginationType;
  nextPageToken?: string;
  stretch?: boolean;
  bg?: string;
  overflow?: LayoutProps["overflow"];
  maxWidth?: string;
  variant?: "intentsifyPlain" | "intentsifyClean" | "intentsifyNew";
  hideHead?: boolean;
  hidePagination?: boolean;
  hidePageSize?: boolean;
  defaultSort?: SortingState;
  defaultPageSize?: number;
  defaultPageIndex?: number;

  // Opt-in persisted column show/hide; also the store key (see
  // useTableColumnVisibility). Pair with a TableColumnsToggle sharing the id.
  columnSettingsId?: string;

  isControlledByAPI?: boolean;
  total?: number;
  onFetchDataChange?: (params: FetchDataParams<keyof T> | any) => void;
  renderExpandableRowComponent?: (row: RowType<T>) => React.ReactNode;

  /**
   * An array of useEffect dependencies you can include to force reseting pagination
   * to first page. For example, you have a global campaingId filter, when it changes
   * you want the table to re-render on the first page.
   */
  resetPaginationDependencies?: any[];
  borderRadius?: ChakraProps["borderRadius"];
  headerCellVerticalAlign?: ChakraProps["verticalAlign"];
  isCellContentCentered?: boolean;
} & RowSelectionProps<T>;

export type RowSelectionProps<T extends Record<string, unknown>> =
  | {
      selectable: true;
      rowSelection: RowSelectionState;
      setRowSelection: React.Dispatch<React.SetStateAction<RowSelectionState>>;
      uniqueKeyAccessor: (item: T) => T[keyof T];
    }
  | {
      selectable?: false;
      rowSelection?: RowSelectionState;
      setRowSelection?: React.Dispatch<React.SetStateAction<RowSelectionState>>;
      uniqueKeyAccessor?: (item: T) => T[keyof T];
    };

const Table = <T extends Record<string, unknown>>({
  dataId,
  data,
  meta,
  columns,
  searchValue,
  noDataMessage,
  customNoData,
  showNoData = true,

  paginationType = PaginationType.LIMIT_OFFSET,
  nextPageToken = "",
  isFetching = false,
  isScrollable = true,
  isExpandable = false,
  stretch = false,
  bg = "inherit",
  overflow = "hidden",
  maxWidth = undefined,
  variant,
  hideHead = false,
  hidePagination = false,
  hidePageSize = false,
  defaultSort = [],
  defaultPageSize = 10,
  defaultPageIndex = 0,
  columnSettingsId,

  isControlledByAPI = true,
  total,
  onFetchDataChange,

  renderExpandableRowComponent,

  resetPaginationDependencies = [],
  borderRadius = "md",
  headerCellVerticalAlign,
  isCellContentCentered = false,

  selectable,
  uniqueKeyAccessor,
  setRowSelection,
  rowSelection,
}: TableProps<T>) => {
  const tableVariant = variant
    ? variant
    : isExpandable
    ? "intentsifyExpanded"
    : "intentsifyStriped";

  const store = useAppStore();

  const storePagination = (clearMapping = false) => {
    if (dataId) {
      const paginationMap = clearMapping ? {} : tokenPagination.getMapping();
      const storedData = {
        paginationMap,
        currentPage: instance.getState().pagination.pageIndex,
        search: searchValue,
        sorting,
      };

      store.setNavForId(dataId, storedData);
    }
  };

  const getStoredPagination = () => {
    if (dataId && searchValue !== "") {
      const storedData = store.getNavForId(dataId);
      if (storedData) {
        return {
          paginationMap: storedData.paginationMap,
          currentPage: storedData.currentPage,
          search: storedData.search,
          sorting: storedData.sorting,
        };
      }
    }

    return {};
  };

  const clearStoredPagination = () => {
    if (dataId) {
      store.clearNavForId(dataId);
    }
  };

  const storedPagination = getStoredPagination();

  const [sorting, setSorting] = useState<SortingState>(
    storedPagination.sorting ?? defaultSort
  );

  const [{ pageIndex, pageSize }, setPagination] = useState<PaginationState>({
    pageIndex: storedPagination.currentPage ?? defaultPageIndex,
    pageSize: defaultPageSize,
  });

  const [expanded, setExpanded] = useState<ExpandedState>({});

  const pagination = useMemo(
    () => ({
      pageIndex,
      pageSize,
    }),
    [pageIndex, pageSize]
  );

  const { hiddenColumnIds } = useTableColumnVisibility(columnSettingsId);

  const columnVisibility = useMemo(() => {
    if (!columnSettingsId || hiddenColumnIds.length === 0) {
      return {};
    }

    const nonHideable = new Set(
      columns
        .filter((column) => !isColumnHideable(column))
        .map(getColumnId)
        .filter((id): id is string => id !== undefined)
    );

    return Object.fromEntries(
      hiddenColumnIds
        .filter((id) => !nonHideable.has(id))
        .map((id) => [id, false])
    );
  }, [columnSettingsId, hiddenColumnIds, columns]);

  const instance = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    getSortedRowModel: getSortedRowModel(),
    state: {
      sorting,
      pagination,
      expanded,
      rowSelection,
      columnVisibility,
    },
    defaultColumn: {
      minSize: 0,
      size: 0,
    },
    enableRowSelection: selectable,
    enableMultiRowSelection: selectable,
    getRowId: uniqueKeyAccessor
      ? (item) => String(uniqueKeyAccessor(item))
      : undefined,
    onRowSelectionChange: setRowSelection,
    onSortingChange: setSorting,
    onPaginationChange: setPagination,
    onExpandedChange: setExpanded,
    manualSorting: isControlledByAPI,
    manualFiltering: isControlledByAPI,
    ...getTablePaginationParams(isControlledByAPI, meta),
  });

  const tokenPagination = useTokenPagination(
    storedPagination.currentPage ?? instance.getState().pagination.pageIndex,
    storedPagination.paginationMap ?? {}
  );

  tokenPagination.useUpdateToken(nextPageToken);

  // Derive globalFilter directly from searchValue prop to ensure it's available on first render.
  // The useEffect syncs this to the table instance for internal filtering.
  const globalFilter = searchValue === "" ? undefined : searchValue;

  useEffect(() => {
    instance.setGlobalFilter(globalFilter);
  }, [globalFilter, instance]);

  useDeepEffect(() => {
    if (isControlledByAPI) {
      if (paginationType === PaginationType.LIMIT_OFFSET) {
        const fetchDataParams = getFetchDataParams<T>({
          pageIndex,
          pageSize,
          sortBy: sorting,
          globalFilter,
        });

        onFetchDataChange?.(fetchDataParams);
      } else if (paginationType === PaginationType.TOKEN) {
        if (dataId) {
          storePagination();
        }

        const { currentPage, paginationMap } = getStoredPagination();
        let token = tokenPagination.currentToken;

        if (paginationMap && Number.isInteger(currentPage)) {
          token = paginationMap[currentPage!]; //eslint-disable-line
        }

        const fetchDataParams = getTokenFetchDataParams<T>({
          pageToken: token,
          sortBy: sorting,
          globalFilter,
        });

        onFetchDataChange?.(fetchDataParams);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    sorting,
    globalFilter,
    pageIndex,
    pageSize,
    onFetchDataChange,
    isControlledByAPI,
  ]);

  const isFirstResetRef = useRef(true);

  useDeepEffect(() => {
    // Skip only the initial mount: the params-emitting effect above already
    // issues the first fetch, so resetting here on mount would double-fetch.
    // (Previously this gated on `isFetching`, which — because isFetching is not
    // a dependency and the effect never re-ran once it settled — permanently
    // dropped any filter/sort/search change made while a fetch was in flight.)
    if (isFirstResetRef.current) {
      isFirstResetRef.current = false;
      return;
    }

    clearStoredPagination();

    setPagination({
      pageIndex: 0,
      pageSize: pagination.pageSize,
    });

    if (paginationType === PaginationType.LIMIT_OFFSET) {
      const fetchDataParams = getFetchDataParams<T>({
        pageIndex: 0,
        pageSize,
        sortBy: sorting,
        globalFilter,
      });

      onFetchDataChange?.(fetchDataParams);
    } else {
      if (dataId) {
        tokenPagination.clearPagination();
        storePagination(true);
      }

      const fetchDataParams = getTokenFetchDataParams<T>({
        pageToken: "",
        sortBy: sorting,
        globalFilter,
      });

      onFetchDataChange?.(fetchDataParams);
    }
  }, [...resetPaginationDependencies, sorting]);

  if (isFetching) {
    return <Loader stretch={stretch} />;
  }

  const selected = Object.keys(instance.getState().rowSelection ?? {});

  return (
    <ErrorBoundary>
      <Flex
        bg={data.length ? bg : undefined}
        rounded={borderRadius}
        maxWidth={maxWidth}
        overflow={overflow}
        direction="column"
        flexGrow={stretch ? 1 : undefined}
        justifyContent={stretch ? "space-between" : undefined}
      >
        {!data.length && showNoData ? (
          <>
            {customNoData ? (
              customNoData
            ) : (
              <NoData stretch={stretch} message={noDataMessage} />
            )}
          </>
        ) : (
          <div
            style={
              isScrollable
                ? {
                    overflowX: "auto",
                    height: "100%",
                    willChange: "scroll-position",
                  }
                : { overflow: "none", height: "100%" }
            }
          >
            <ChakraTable
              fontWeight={"md"}
              display={{
                base: "block",
                md: "table",
              }}
              sx={{
                "@media print": {
                  display: "table",
                },
                overflowY: "scroll",
                height: stretch ? "100%" : "auto",
              }}
              size="sm"
              variant={tableVariant}
              shadow={byLook(undefined, "sm")}
            >
              {!hideHead && (
                <Thead>
                  {instance.getHeaderGroups().map((headerGroup) => (
                    <HeaderGroup
                      key={headerGroup.id}
                      headerGroup={headerGroup}
                      isCentered={isCellContentCentered}
                      verticalAlign={headerCellVerticalAlign}
                    />
                  ))}
                </Thead>
              )}

              <Tbody>
                {instance.getRowModel().rows.map((row) => (
                  <Row<T>
                    key={row.id}
                    row={row}
                    renderExpandableRowComponent={renderExpandableRowComponent}
                    colSpan={columns.length + 1}
                    isCentered={isCellContentCentered}
                  />
                ))}
              </Tbody>
            </ChakraTable>
          </div>
        )}

        <Flex alignItems="center">
          {Boolean(selected.length) && (
            <Flex alignItems="center">
              <Text whiteSpace="nowrap" fontSize="md">
                Rows selected: <b>{selected.length}</b>
              </Text>
              <Button
                onClick={() => {
                  setRowSelection?.({});
                }}
                ml={2}
                variant="outline"
                size="xs"
              >
                Clear
              </Button>
            </Flex>
          )}

          {!hidePagination && paginationType === PaginationType.TOKEN && (
            <TokenPagination
              onPrevPage={() =>
                instance.setPageIndex(
                  instance.getState().pagination.pageIndex - 1
                )
              }
              isPrevDisabled={instance.getState().pagination.pageIndex === 0}
              onNextPage={() =>
                instance.setPageIndex(
                  instance.getState().pagination.pageIndex + 1
                )
              }
              isNextDisabled={nextPageToken === ""}
            />
          )}

          {!hidePagination &&
            paginationType === PaginationType.LIMIT_OFFSET && (
              <Pagination
                key={Math.random()}
                hidePageSize={hidePageSize}
                total={
                  total ||
                  instance.getPageCount() *
                    instance.getState().pagination.pageSize
                }
                current={instance.getState().pagination.pageIndex + 1}
                pageSize={instance.getState().pagination.pageSize}
                goToPage={(page) => instance.setPageIndex(page)}
                setPageSize={(size) => instance.setPageSize(size)}
              />
            )}
        </Flex>
      </Flex>
    </ErrorBoundary>
  );
};

export { Table };
